import styled, { css } from 'styled-components';
import { fontWeight } from 'styles/common';
import colors, { opacity } from 'styles/colors';
import { PhoneInput } from 'react-international-phone';

import WithPlaceholder from './WithPlaceholder.hoc';

import { Input as FormInput, INPUT_STYLES_MIXIN, INPUT_FOCUS_MIXIN, InputTextIndicator, Textarea } from 'styles/form';
import { DatePickerWrapper, TimePickerWrapper } from 'components/DateTimePicker/styles';
import { LOADING } from 'styles/commonClasses';
import { RefreshSpinMixin } from 'styles/animation';

import 'react-international-phone/style.css';

export const INPUT_CLASSES = {
  HAS_VALUE: 'has_value',
  HAS_FOCUS: 'has_focus',
  HAS_ERROR: 'has_error',
};

export const FieldContainer = styled.div<{ visible?: boolean }>`
  transition: max-height 0.15s linear;
  text-align: left;
  width: 100%;

  ${(props) => props.visible === false && `display: none;`};

  ${DatePickerWrapper}, ${TimePickerWrapper} {
    ${INPUT_STYLES_MIXIN};
    height: 30px;
    display: flex;
    align-items: center;

    input {
      color: ${colors.inactive};
      margin: 0 0 0 1em;
      height: 1em;
    }

    span {
      color: ${colors.inactive};
    }
  }

  &.${INPUT_CLASSES.HAS_VALUE} {
    ${DatePickerWrapper}, ${TimePickerWrapper} {
      input {
        color: ${colors.primary};
      }
    }
  }

  &.${INPUT_CLASSES.HAS_FOCUS} {
    ${DatePickerWrapper}, ${TimePickerWrapper} {
      ${INPUT_FOCUS_MIXIN};
    }
  }

  &.${INPUT_CLASSES.HAS_ERROR} {
    input {
      border-color: ${colors.danger};
      background-color: ${colors.dangerBackground};
      color: ${colors.danger};
    }

    input::placeholder {
      color: ${colors.dangerPlaceholder};
    }
  }
`;

export const FieldValue = styled.div<{ hasValue?: boolean }>`
  font-size: 13px;
  margin: 0.1em 0 0 0;
  word-break: break-all;

  ${(props) => !props.hasValue && `color: ${colors.inactive};`} ${FieldContainer}.error & {
    color: ${colors.danger};
  }

  ${FieldContainer}.warning & {
    color: ${colors.warning};
  }
`;

export const FieldInputWrapper = styled.div`
  width: 100%;
`;

export const FieldRow = styled.div`
  display: flex;
  justify-content: start;
  margin-bottom: 1em;
`;

export const FieldInput = styled(FormInput)`
  width: 100%;
  height: 30px;
`;

export const NoteFieldInput = styled(Textarea)`
  width: 100%;
  height: 159px;
`;

export const PhoneFieldInput = styled(PhoneInput)<{ readOnly?: boolean; dark?: boolean }>`
  width: 100%;

  .react-international-phone-country-selector {
    button {
      height: 30px !important;
    }

    ul {
      top: 30px !important;
    }

    ${({ readOnly }) =>
      readOnly &&
      css`
        display: none;
      `};
  }

  input {
    width: calc(100% - 20px);
    height: 30px !important;

    ${(props) =>
      props.readOnly &&
      css`
        border: none !important;
        font-weight: ${fontWeight.semibold};
      `};

    &:not([disabled]) {
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }

    &:read-only:not([disabled]) {
      padding: 0 !important;
    }

    &:disabled:read-only {
      background: #dadee6 !important;
      border-color: #0000;
      box-shadow: none;
      color: ${colors.primary};
      opacity: 1;
    }
  }

  ${({ dark }) =>
    dark &&
    `.react-international-phone-input {
      background-color: #161926;
      border-color: ${opacity(colors.fleetOffGray, 0.2)};
      color: ${colors.white};
      font-size: 16px;
    }

    .react-international-phone-country-selector-button {
      background-color: ${opacity('#161926', 0.9)};
      border-color: ${opacity(colors.fleetOffGray, 0.2)};
    }

    .react-international-phone-country-selector-button--active {
      background-color: ${opacity(colors.black, 0.4)};
    }

    .react-international-phone-country-selector-dropdown {
      background-color: #161926;
      color: ${colors.white};
    }

    .react-international-phone-country-selector-dropdown__list-item {
      background-color: #161926;
      color: ${colors.white};
    }

    .react-international-phone-country-selector-dropdown__list-item--selected {
      background-color: ${opacity(colors.fleetOffGray, 0.2)};
      color: ${colors.white};
    }
    .react-international-phone-country-selector-dropdown__list-item--focused {
      background-color: ${opacity(colors.fleetOffGray, 0.2)};
      color: ${colors.white};
    }
  `};
`;

const DigitInput = styled(FieldInput)`
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const TwoDigitInput = WithPlaceholder(styled(DigitInput)`
  margin-right: 10px;
  width: 80px;
`);

export const FourDigitInput = WithPlaceholder(styled(DigitInput)`
  margin-right: 10px;
  width: 80px;
`);

export const MultiInputWrapper = styled.div`
  color: ${colors.fieldLabel};
  align-content: space-between;
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const DateInputWrapper = styled.div`
  align-content: center;
  align-items: center;
  display: flex;
  justify-content: left;
  max-width: 285px;

  ${DigitInput}:last-of-type {
    margin-right: 8px;
  }

  button {
    font-size: 1.5em;
    left: -3px;
    position: relative;
    color: ${colors.gray55};
  }

  ${MultiInputWrapper} {
    flex-grow: 1;
  }
`;

export const CountryDropdownWrapper = styled(FieldInputWrapper)``;
export const InsuranceNumberWrapper = styled(FieldInputWrapper)``;
export const RegionDropdownWrapper = styled(FieldInputWrapper)``;
export const LicenseNumberWrapper = styled(FieldInputWrapper)``;
export const ZipOrPostalCodeWrapper = styled(FieldInputWrapper)``;

export const ConnectedMetadataWrapper = styled.div`
  font-size: 11px;
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: space-between;
  margin-top: 8px;
  width: 100%;

  i {
    font-size: 18px;
    margin-left: 1px;
  }
`;

export const ConnectedMetadataText = styled.div`
  margin-left: 5px;
  max-width: 170px;
  line-height: 1.33em;

  i {
    font-size: 0.8em;
  }
`;

export const RefreshButtonWrapper = styled.div`
  align-items: center;
  display: inline-flex;
  flex-direction: row;
  margin-left: 0.5em;
  position: relative;
  height: 1em;
  width: 1em;

  button {
    font-size: 1em;
  }

  button.${LOADING} {
    margin: 0;
    transform-origin: 50% 50%;
    ${RefreshSpinMixin};
    height: 1em;
    width: 1em;
  }
`;

export const ActionText = styled.div<{ visible?: boolean }>`
  color: ${colors.fleetBlue};
  font-weight: 600;
  position: absolute;
  right: 0;
  cursor: pointer;
  ${(props) => props.visible === false && 'display: none;'}

  &:hover {
    color: ${colors.highlightActive};
  }
`;
